<script setup lang="ts">
import useCustomOfferModalStore from '~/stores/modals/useCustomOfferModal'
import type { EstateDetailInterface } from '~/types/api/EstateDetail'

const props = defineProps<{
  estateData: EstateDetailInterface
}>()
const { t, te, locale } = useI18n()
const customOfferModalStore = useCustomOfferModalStore()

function getOfficeTableData() {
  const headings = [
    {
      size: 170,
      text: t('pages.estateDetail.availableSpaces.table.header.floor'),
    },
    {
      size: 130,
      text: t('pages.estateDetail.availableSpaces.table.header.type'),
    },
    {
      size: 140,
      text: t('pages.estateDetail.availableSpaces.table.header.availability'),
    },
    {
      size: 130,
      text: t('pages.estateDetail.availableSpaces.table.header.size'),
    },
    {
      size: 200,
      text: t('pages.estateDetail.availableSpaces.table.header.price', {
        _0: 'm<sup>2</sup>',
      }),
    },
  ]
  const rows = []

  const sortedUnits = [...props.estateData.office_units].sort((a, b) => {
    if (b.floor_num - a.floor_num === 0) {
      return b.floor.localeCompare(a.floor)
    }

    return b.floor_num - a.floor_num
  })

  for (const unit of sortedUnits) {
    let floor

    if (unit.floor_num === 0) {
      floor = locale.value === 'sk' ? unit.floor_text : unit.floor_text_en
    } else {
      floor = t('pages.estateDetail.availableSpaces.table.values.floor', {
        _0: unit.floor_num,
      })
    }

    const type = te(
      `pages.estateDetail.availableSpaces.table.values.type.${unit.type}`
    )
      ? t(`pages.estateDetail.availableSpaces.table.values.type.${unit.type}`)
      : unit.type || '-'

    const availability = unit.availability_text || '-'
    const size = unit.size ? `${unit.size} m<sup>2</sup>` : '-'
    let rent = '-'

    if (!unit.asking_rent_from) {
      rent = '-'
    } else if (unit.asking_rent_from === unit.asking_rent_to) {
      rent = formatPrice(unit.asking_rent_from)
    } else if (unit.asking_rent_from && unit.asking_rent_to) {
      rent = `${formatNumber(unit.asking_rent_from)} - ${formatPrice(
        unit.asking_rent_to
      )}`
    }

    rows.push({
      floor,
      type,
      availability,
      size: `<strong>${size}</strong>`,
      rent: `<strong>${rent}</strong>`,
    })
  }

  return {
    headings,
    rows,
  }
}

function getOtherTableData() {
  const headings = [
    {
      size: 150,
      text: t('pages.estateDetail.availableSpaces.table.header.name'),
    },
    {
      size: 150,
      text: t('pages.estateDetail.availableSpaces.table.header.size'),
    },
    {
      size: 150,
      text: t('pages.estateDetail.availableSpaces.table.header.status'),
    },
  ]
  const rows = []

  for (const unit of props.estateData.office_units) {
    const name = unit.unit_name || '-'
    const size = unit.size ? `${unit.size} m<sup>2</sup>` : '-'
    const status = te(
      `pages.estateDetail.availableSpaces.table.values.unit_status.${unit.unit_status}`
    )
      ? t(
          `pages.estateDetail.availableSpaces.table.values.unit_status.${unit.unit_status}`
        )
      : unit.unit_status || '-'

    rows.push({
      name,
      size: `<strong>${size}</strong>`,
      status,
    })
  }

  return {
    headings,
    rows,
  }
}

const tableData = computed(() => {
  if (!props.estateData.office_units.length) {
    return null
  }

  if (props.estateData.type === 'office') {
    return getOfficeTableData()
  }

  if (props.estateData.type === 'retail') {
    return getOtherTableData()
  }

  return null
})
</script>

<template>
  <div v-if="tableData" class="c-estate-available-spaces">
    <h2 class="c-estate-available-spaces__title h4">
      {{
        $t('pages.estateDetail.availableSpaces.title', { _0: estateData.name })
      }}
    </h2>

    <i18n-t
      keypath="pages.estateDetail.availableSpaces.subtitle.base"
      tag="div"
      scope="global"
      class="c-estate-available-spaces__sub-title"
    >
      <template #_0>
        <strong>{{
          $t('pages.estateDetail.availableSpaces.subtitle.0')
        }}</strong>
      </template>

      <template #_1>
        <button
          class="c-link c-link--underlined-bold"
          @click="customOfferModalStore.open()"
        >
          <strong>{{
            $t('pages.estateDetail.availableSpaces.subtitle.1')
          }}</strong>
        </button>
      </template>
    </i18n-t>

    <div
      :class="[
        'c-estate-available-spaces__table',
        'c-estate-available-spaces-table',
        props.estateData.type === 'office' && 'is-office',
      ]"
    >
      <div class="c-estate-available-spaces-table__head">
        <div
          v-for="(headingData, index) in tableData.headings"
          :key="`estate-table-head-${index}`"
          class="c-estate-available-spaces-table__cell c-estate-available-spaces-table__cell--heading"
          :style="{
            '--size': `${headingData.size}px`,
          }"
          v-html="headingData.text"
        />
      </div>

      <div class="c-estate-available-spaces-table__body">
        <div
          v-for="(row, index) in tableData.rows"
          :key="index"
          class="c-estate-available-spaces-table__row"
        >
          <div
            v-for="(value, index) in Object.entries(row)"
            :key="`estate-table-body-${index}`"
            class="c-estate-available-spaces-table__cell c-estate-available-spaces-table__cell--body"
            :style="{
              '--size': `${tableData.headings[index].size}px`,
            }"
            v-html="value[1]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-estate-available-spaces {
  &__title {
    margin-bottom: 18px;
  }

  &__sub-title {
    margin-bottom: 18px;
  }

  &__table {
    overflow: auto;

    @include breakpoint-down('sm') {
      margin-left: var(--container-padding-negative);
      margin-right: var(--container-padding-negative);
    }

    table {
      width: 100%;
      white-space: nowrap;

      th {
        background: var(--color-solid-grey);
        font-weight: 600;
      }

      th,
      td {
        padding: 12px 18px;
        text-align: left;

        &:first-child {
          padding-left: 32px;
        }
        &:last-child {
          padding-right: 32px;
        }

        @include breakpoint-down('sm') {
          border-bottom: 1px solid var(--color-border);

          &:first-child {
            padding-left: var(--container-padding);
          }
          &:last-child {
            padding-right: var(--container-padding);
          }
        }
      }

      tr:not(:last-child) td {
        border-bottom: 1px solid var(--color-border);
      }
    }
  }
}

.c-estate-available-spaces-table {
  font-size: 18px;
  line-height: 1;

  @supports (grid-template-columns: subgrid) {
    display: grid;
    grid-template-columns: repeat(3, auto);
    white-space: nowrap;

    &.is-office {
      grid-template-columns: repeat(5, auto);
    }
  }
  @supports not (grid-template-columns: subgrid) {
    display: flex;
    flex-direction: column;
  }

  &__head {
    margin-bottom: 10px;
    background-color: var(--color-solid-grey);
    font-weight: 600;

    @supports (grid-template-columns: subgrid) {
      display: grid;
      grid-column: 1 / -1;
      grid-template-columns: subgrid;
    }
    @supports not (grid-template-columns: subgrid) {
      display: grid;
      grid-auto-flow: column;
      width: fit-content;
    }
  }

  &__body {
    @supports (grid-template-columns: subgrid) {
      display: grid;
      grid-column: 1 / -1;
      grid-template-columns: subgrid;
    }
    @supports not (grid-template-columns: subgrid) {
      display: flex;
      flex-direction: column;
    }
  }

  &__row {
    @supports (grid-template-columns: subgrid) {
      display: grid;
      grid-template-columns: subgrid;
      grid-column: 1 / -1;
    }
    @supports not (grid-template-columns: subgrid) {
      display: grid;
      grid-auto-flow: column;
      width: fit-content;
    }

    &:not(:last-child) {
      border-bottom: 1px solid var(--color-border);
    }
  }

  &__cell {
    padding: 16px 12px;
    text-align: right;

    @supports not (grid-template-columns: subgrid) {
      width: var(--size, 150px);
      overflow-wrap: break-word;
    }

    &:first-child {
      padding-left: 32px;
      text-align: left;
    }

    &:nth-child(2) {
      text-align: left;
    }

    &:last-child {
      padding-right: 32px;
    }

    &--heading {
      padding-top: 18px;
      padding-bottom: 18px;
    }

    @include breakpoint-down('sm') {
      padding-left: 16px;
      padding-right: 16px;
    }
  }
}
</style>
